import React, { useEffect, useState } from "react";
import api from "@/lib/api";
import { fmtDate } from "@/lib/format";
import { Card } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { toast } from "sonner";
import { Boxes, Save, History } from "lucide-react";

const KIND_LABEL = {
  set: "Owner set",
  hp_invoice: "HP invoice (in)",
  driver_load: "Driver loaded (out)",
  driver_return: "Return (in)",
};

const fmtTime = (s) => {
  if (!s) return "—";
  try {
    const dt = new Date(s);
    return `${fmtDate(s.slice(0, 10))} ${dt.toLocaleTimeString("en-IN", { hour: "2-digit", minute: "2-digit" })}`;
  } catch {
    return s;
  }
};

export default function CylinderStock() {
  const [rows, setRows] = useState([]);
  const [moves, setMoves] = useState([]);
  const [inputs, setInputs] = useState({});
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState("");

  const load = async () => {
    setLoading(true);
    try {
      const [s, m] = await Promise.all([
        api.get("/cylinder-stock"),
        api.get("/cylinder-stock/moves"),
      ]);
      setRows(s.data);
      setMoves(m.data);
      const seed = {};
      s.data.forEach((r) => { seed[r.cylinder_type] = { value: String(r.stock), note: "" }; });
      setInputs(seed);
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => { load(); }, []);

  const save = async (r) => {
    const inp = inputs[r.cylinder_type] || {};
    const val = parseInt(inp.value, 10);
    if (isNaN(val) || val < 0) { toast.error("Enter a valid count (0 or more)"); return; }
    setBusy(r.cylinder_type);
    try {
      await api.post("/cylinder-stock/set", {
        cylinder_type: r.cylinder_type,
        stock: val,
        note: inp.note || "",
      });
      toast.success(`${r.label} stock updated to ${val}`);
      await load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to update");
    } finally {
      setBusy("");
    }
  };

  const setField = (code, field, value) =>
    setInputs((prev) => ({ ...prev, [code]: { ...(prev[code] || {}), [field]: value } }));

  return (
    <div className="space-y-6 fade-up" data-testid="cylinder-stock-page">
      <div>
        <h1 className="font-head text-2xl font-bold flex items-center gap-2">
          <Boxes className="h-6 w-6 text-primary" /> Cylinder stock
        </h1>
        <p className="text-sm text-muted-foreground max-w-2xl">
          Set your godown opening count for each cylinder type. After that it moves on its own —
          up when the office records HP filled-cylinder invoices, down when drivers load, and back up on returns.
        </p>
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
        {loading && [0, 1, 2, 3].map((i) => (
          <Card key={i} className="p-5 h-40 animate-pulse bg-muted/40" />
        ))}
        {!loading && rows.map((r) => {
          const inp = inputs[r.cylinder_type] || {};
          return (
            <Card key={r.cylinder_type} className="p-5" data-testid={`stock-card-${r.cylinder_type}`}>
              <div className="flex items-start justify-between">
                <div>
                  <div className="text-sm font-medium text-muted-foreground">{r.label}</div>
                  <div className="kpi-number text-3xl font-semibold tabular-nums mt-1" data-testid={`stock-current-${r.cylinder_type}`}>
                    {r.stock}
                  </div>
                  <div className="text-xs text-muted-foreground mt-0.5">in godown now</div>
                </div>
                <Badge variant="outline" className="font-mono">{r.cylinder_type}</Badge>
              </div>
              <div className="mt-4 space-y-3">
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <Label className="text-xs">Set count</Label>
                    <Input
                      inputMode="numeric"
                      className="font-mono"
                      value={inp.value ?? ""}
                      onChange={(e) => setField(r.cylinder_type, "value", e.target.value.replace(/\D/g, ""))}
                      data-testid={`stock-input-${r.cylinder_type}`}
                    />
                  </div>
                  <div>
                    <Label className="text-xs">Reason (optional)</Label>
                    <Input
                      placeholder="e.g. opening count"
                      value={inp.note ?? ""}
                      onChange={(e) => setField(r.cylinder_type, "note", e.target.value)}
                      data-testid={`stock-note-${r.cylinder_type}`}
                    />
                  </div>
                </div>
                <Button
                  className="w-full gap-2"
                  disabled={busy === r.cylinder_type}
                  onClick={() => save(r)}
                  data-testid={`stock-save-${r.cylinder_type}`}
                >
                  <Save className="h-4 w-4" /> {busy === r.cylinder_type ? "Saving…" : "Save count"}
                </Button>
                {r.updated_at && (
                  <div className="text-[11px] text-muted-foreground">
                    Last updated {fmtTime(r.updated_at)} by {r.updated_by}
                  </div>
                )}
              </div>
            </Card>
          );
        })}
      </div>

      <Card className="p-0 overflow-hidden">
        <div className="flex items-center gap-2 p-4 border-b">
          <History className="h-4 w-4 text-muted-foreground" />
          <h2 className="text-lg font-semibold">Stock movement history</h2>
        </div>
        <div className="overflow-x-auto">
          <Table data-testid="stock-moves-table">
            <TableHeader>
              <TableRow>
                <TableHead>When</TableHead>
                <TableHead>Cylinder</TableHead>
                <TableHead>Change</TableHead>
                <TableHead className="text-right">Qty</TableHead>
                <TableHead>Reason</TableHead>
                <TableHead>By</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {loading && <TableRow><TableCell colSpan={6} className="text-center py-8 text-muted-foreground">Loading…</TableCell></TableRow>}
              {!loading && moves.length === 0 && (
                <TableRow><TableCell colSpan={6} className="text-center py-8 text-muted-foreground">No stock movements yet. Set an opening count to begin.</TableCell></TableRow>
              )}
              {!loading && moves.map((m) => (
                <TableRow key={m.id}>
                  <TableCell className="text-xs text-muted-foreground whitespace-nowrap">{fmtTime(m.at)}</TableCell>
                  <TableCell className="font-medium">{m.label}</TableCell>
                  <TableCell>{KIND_LABEL[m.kind] || m.kind}</TableCell>
                  <TableCell className={`text-right tabular-nums font-semibold ${m.delta < 0 ? "text-rose-600" : "text-emerald-600"}`}>
                    {m.delta > 0 ? `+${m.delta}` : m.delta}
                  </TableCell>
                  <TableCell className="text-muted-foreground">{m.note || "—"}</TableCell>
                  <TableCell className="text-muted-foreground">{m.by}</TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </div>
      </Card>
    </div>
  );
}
